import { z } from 'zod'

/**
 * Per-shop settings, stored as JSON on `shops.settings_json` and mirrored into KV for the
 * storefront widget.
 *
 * Every field here is a support surface. The bias is a good default plus an explanation,
 * not a toggle, see SW-604. Nothing is added to this schema without a merchant asking twice.
 */

export const alertSettingsSchema = z.object({
  /**
   * Minimum available quantity before a restock is worth alerting on. Restocking three units
   * of a SKU 4,000 people are waiting on means 3,997 people click through to a sold-out page,
   * which burns trust in the alert itself, worse than sending nothing (SW-403).
   */
  minRestockQuantity: z.number().int().min(1).max(10_000).default(1),
  /**
   * Alert only the first N subscribers, oldest first. The companion control to the quantity
   * threshold, and the honest answer to a small restock.
   */
  notifyFirstN: z.number().int().min(1).max(1_000_000).nullable().default(null),
  /** A variant that alerted cannot alert again within this window, regardless of movement. */
  variantCooldownHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 30)
    .default(24),
  /** Belt-and-braces cap against a bulk inventory import turning into a send storm. */
  dailyAlertCap: z.number().int().min(1).max(1_000_000).default(50_000),
  excludedProductIds: z.array(z.string()).max(5_000).default([]),
  excludedCollectionIds: z.array(z.string()).max(1_000).default([]),
})

export const sendingSettingsSchema = z.object({
  fromName: z.string().trim().min(1).max(78).nullable().default(null),
  replyTo: z.email().nullable().default(null),
  /** Delegated-DKIM sending domain. Paid-tier upgrade over the shared domain (SW-501). */
  customSendingDomain: z.string().trim().toLowerCase().max(253).nullable().default(null),
  customSendingDomainVerified: z.boolean().default(false),
  /**
   * Quiet window in the *subscriber's* local hours. Off by default and warned about in the
   * UI: a restock alert delayed eight hours is worthless because the stock is gone (SW-504).
   */
  sendingWindow: z
    .object({
      enabled: z.boolean().default(false),
      startHour: z.number().int().min(0).max(23).default(8),
      endHour: z.number().int().min(0).max(23).default(22),
    })
    .prefault({}),
  /** Required in the email footer by CAN-SPAM. Blocks sending when absent. */
  postalAddress: z.string().trim().max(500).nullable().default(null),
})

export const consentSettingsSchema = z.object({
  /**
   * Off by default for email. Double opt-in roughly halves list size and merchants read that
   * as the app being broken. Mandatory for SMS regardless of this flag (SW-902).
   */
  doubleOptInEmail: z.boolean().default(false),
  doubleOptInSms: z.literal(true).default(true),
  consentCopy: z
    .string()
    .trim()
    .max(1_000)
    .default('Email me once when this is back in stock. No marketing, no sharing your address.'),
  consentCopyVersion: z.string().trim().max(40).default('v1'),
  smsConsentCopy: z
    .string()
    .trim()
    .max(1_000)
    .default(
      'I agree to receive one automated back-in-stock text for this item. Msg & data rates may apply. Reply STOP to opt out, HELP for help.',
    ),
  smsConsentCopyVersion: z.string().trim().max(40).default('v1'),
})

export const widgetSettingsSchema = z.object({
  enabled: z.boolean().default(true),
  buttonText: z.string().trim().min(1).max(60).default('Notify me when available'),
  buttonBackground: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#1a1a1a'),
  buttonTextColour: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#ffffff'),
  borderRadius: z.number().int().min(0).max(40).default(6),
  /**
   * auto:   inject next to the add-to-cart button using the theme heuristics in SW-302
   * manual: merchant-supplied CSS selector, the escape hatch that turns a 1-star review into
   *         a five-minute support reply
   * block:  merchant drags the app block into place in the theme editor
   */
  placement: z.enum(['auto', 'manual', 'block']).default('auto'),
  manualSelector: z.string().trim().max(300).nullable().default(null),
  showOnCollectionPages: z.boolean().default(false),
  showOnQuickView: z.boolean().default(true),
  modalHeading: z.string().trim().min(1).max(120).default('Get notified'),
  successMessage: z
    .string()
    .trim()
    .min(1)
    .max(240)
    .default("You're on the list. We'll email you the moment it's back."),
  alreadySubscribedMessage: z
    .string()
    .trim()
    .min(1)
    .max(240)
    .default("You're already on the list for this one; we'll be in touch."),
})

export const channelSettingsSchema = z.object({
  /** email-only until SW-902's registration is approved; the router enforces it (SW-903). */
  strategy: z.enum(['email_only', 'sms_only', 'both', 'sms_high_value']).default('email_only'),
  /** Minimum item price, in minor units, for the sms_high_value strategy. */
  smsHighValueThresholdMinor: z.number().int().min(0).default(10_000),
  smsEnabled: z.boolean().default(false),
  whatsappEnabled: z.boolean().default(false),
})

export const intelligenceSettingsSchema = z.object({
  /** Weekly restock digest, the retention mechanism for the whole product (SW-803). */
  digestEnabled: z.boolean().default(true),
  /** 1 = Monday … 7 = Sunday, ISO weekday. */
  digestWeekday: z.number().int().min(1).max(7).default(1),
  digestRecipients: z.array(z.email()).max(10).default([]),
  /**
   * Share of sold-out-page visitors who join the waitlist, used to extrapolate total unmet
   * demand from signups (SW-802). Merchant-overridable because storefront traffic is not
   * always available, and loudly labelled an estimate wherever it is used.
   */
  captureRateOverride: z.number().min(0.001).max(1).nullable().default(null),
  attributionWindowHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 30)
    .default(72),
})

export const shopSettingsSchema = z.object({
  alerts: alertSettingsSchema.prefault({}),
  sending: sendingSettingsSchema.prefault({}),
  consent: consentSettingsSchema.prefault({}),
  widget: widgetSettingsSchema.prefault({}),
  channels: channelSettingsSchema.prefault({}),
  intelligence: intelligenceSettingsSchema.prefault({}),
})

export type AlertSettings = z.infer<typeof alertSettingsSchema>
export type SendingSettings = z.infer<typeof sendingSettingsSchema>
export type ConsentSettings = z.infer<typeof consentSettingsSchema>
export type WidgetSettings = z.infer<typeof widgetSettingsSchema>
export type ChannelSettings = z.infer<typeof channelSettingsSchema>
export type IntelligenceSettings = z.infer<typeof intelligenceSettingsSchema>
export type ShopSettings = z.infer<typeof shopSettingsSchema>

export const DEFAULT_SHOP_SETTINGS: ShopSettings = shopSettingsSchema.parse({})

/**
 * Settings are read on the hot send path, so a shop row written by an older app version must
 * never throw here. Unknown or invalid fields fall back to defaults rather than failing the
 * send, a merchant losing one custom button colour is recoverable; a fan-out that throws is
 * a missed restock.
 */
export function parseSettings(json: string | null | undefined): ShopSettings {
  if (!json) return DEFAULT_SHOP_SETTINGS
  try {
    const parsed = shopSettingsSchema.safeParse(JSON.parse(json))
    return parsed.success ? parsed.data : DEFAULT_SHOP_SETTINGS
  } catch {
    return DEFAULT_SHOP_SETTINGS
  }
}

/** Deep-merges a partial update over current settings, without validating. */
function mergeDraft(current: ShopSettings, patch: unknown): Record<string, unknown> {
  const draft = patch as Record<string, Record<string, unknown>> | null
  if (!draft || typeof draft !== 'object') return { ...current }
  const merged: Record<string, unknown> = { ...current }
  for (const [group, values] of Object.entries(draft)) {
    if (!(group in current) || values === null || typeof values !== 'object') continue
    merged[group] = { ...(current as unknown as Record<string, object>)[group], ...values }
  }
  return merged
}

/**
 * One validation issue, addressed by the dotted path the admin UI uses for its fields.
 *
 * `message` is shown to a merchant verbatim, so it says what to do rather than restating the
 * constraint. Zod's own text ("Too small: expected number to be >=1") is a developer message.
 */
export type SettingsIssue = { path: string; message: string }

/**
 * Merchant-facing wording per field, because the generic version of this is useless.
 *
 * SW-70: every one of these produced a bare HTTP 400 and the toast "Could not save settings",
 * with no indication of which field was wrong. Clearing the button text to retype it was enough
 * to hit it, and an App Store reviewer did, on the first save they attempted.
 */
const ISSUE_COPY: Record<string, string> = {
  'alerts.minRestockQuantity': 'Enter 1 or more.',
  'alerts.notifyFirstN': 'Enter 1 or more, or leave it empty to alert everyone.',
  'alerts.variantCooldownHours': 'Enter between 1 and 720 hours.',
  'alerts.dailyAlertCap': 'Enter between 1 and 1,000,000.',
  'sending.fromName': 'Enter a name, or leave it empty to use the default.',
  'sending.replyTo': 'Enter a valid email address, or leave it empty.',
  'sending.customSendingDomain': 'Enter a domain such as mail.yourshop.com.',
  'consent.consentCopy': 'This is too long; keep it under 1,000 characters.',
  'widget.buttonText': 'The button needs some text.',
  'widget.buttonBackground': 'Pick a colour, or enter a hex value such as #1a1a1a.',
  'widget.buttonTextColour': 'Pick a colour, or enter a hex value such as #ffffff.',
  'widget.modalHeading': 'The popup needs a heading.',
  'widget.successMessage': 'Enter the message shown after someone signs up.',
  'widget.alreadySubscribedMessage': 'Enter the message shown to someone already on the list.',
  'widget.borderRadius': 'Enter between 0 and 40.',
  'intelligence.digestWeekday': 'Choose a day of the week.',
  'intelligence.digestRecipients': 'Enter valid email addresses, up to 10.',
  'intelligence.attributionWindowHours': 'Enter between 1 and 720 hours.',
}

/**
 * Validation issues in a candidate settings object. Empty means it will save.
 *
 * Exported so the admin SPA can check a draft against the *same* schema the Worker enforces,
 * rather than reimplementing the rules and drifting from them. The server still validates:
 * this is for telling the merchant which field to fix, not for trusting the client.
 */
export function settingsIssues(candidate: unknown): SettingsIssue[] {
  const parsed = shopSettingsSchema.safeParse(candidate)
  if (parsed.success) return []
  return parsed.error.issues.map((issue) => {
    const path = issue.path.join('.')
    return { path, message: ISSUE_COPY[path] ?? issue.message }
  })
}

/** Deep-merges a partial update, revalidates, and reports what is wrong instead of throwing. */
export function mergeSettingsSafe(
  current: ShopSettings,
  patch: unknown,
): { ok: true; value: ShopSettings } | { ok: false; issues: SettingsIssue[] } {
  const draft = patch as Record<string, unknown> | null
  if (!draft || typeof draft !== 'object') return { ok: true, value: current }
  const merged = mergeDraft(current, patch)
  const parsed = shopSettingsSchema.safeParse(merged)
  if (parsed.success) return { ok: true, value: parsed.data }
  return { ok: false, issues: settingsIssues(merged) }
}
